/**
 * @Description Helper functions for FasatGridBar component
 * @FileName fasatGridBarHelpers.jsx
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 25 January, 2021 18:32:32
 * @IssueID CDTFASATMOD-502
 */
import React from 'react';
import { ActionButton, colors } from '@manulife/mux';
import {
  ButtonCheckmarkOutlined, Coin, Delete, Edit, Export, FundSwitches, Import, Reload,
  RollOverFunds, Write, Email, ButtonXOutlined, Download, Print, Eye1, Eye2, ArrowLeft, Duplicate,
} from '@manulife/mux-cds-icons';
import { FasatIcon } from '../index';
import FasatDropdown from '../fasatDropdown/fasatDropdown';
import FasatChip from '../fasatChip/fasatChip';
import { staticCommonLabelKeys } from '../../moduleConstants';
import { formatOrder, getListRequest } from '../fasatFilterSetting/filterUtil';
import { getAction } from '../../../../stateManagement/reduxUtil';
import FasatRBAC from '../fasatRBAC/fasatRBAC';
import { staticLabelKeys } from '../../../modules/compcal/moduleConstants';
import { setParam } from '../../util';

/**
 * Icon color helper functions
 */
export const icnExtCls = (activExBtn, color) => (!activExBtn ? color.dark_3_light_grey : color.m_green);
export const icnHvExtCls = (actExBtn, color) => (!actExBtn ? color.dark_3_light_grey : color.dark_2_green);
export const reLdExtCls = (restEn, color) => (restEn ? color.m_green : color.dark_3_light_grey);
export const reLdHvExtCls = (restEn, color) => (restEn ? color.dark_2_green : color.dark_3_light_grey);
export const icnAddCls = (activExBtn, color) => (activExBtn ? color.dark_3_light_grey : color.m_green);
export const icnHvAddCls = (actExBtn, color) => (actExBtn ? color.dark_3_light_grey : color.dark_2_green);

/**
 * Utility functions
 */
export const formatListCol = ({ listColumnOrder, selectedFilter, isGroupActive }) => {
  const selListCol = listColumnOrder || selectedFilter.detail.columnOrders || selectedFilter.detail.sortOptions;
  if (isGroupActive && selListCol.indexOf('sumRecordCount') === -1) {
    selListCol.push('sumRecordCount');
  }
  return selListCol;
};

export const setFltDetails = ({ formRequest, relationshipSortOrder, selectedSortOptions, selectedListColumnOrder,
  groupByParams, isGroupActive, isDistinct, relationshipParameter, isQuickSearchParam }) => {
  let fltDetls = {};
  if (formRequest?.parameters) {
    fltDetls = {
      parameters: formRequest?.parameters,
      sortOptions: relationshipSortOrder || selectedSortOptions,
      columnOrder: selectedListColumnOrder,
      groupByParameters: groupByParams,
      groupByActive: isGroupActive,
      isCurrentRate: formRequest?.isCurrentRate?.toString(),
      isNonBandRate: formRequest?.isNonBandRate?.toString(),
      isDistinct,
    };
  } else {
    fltDetls = {
      parameters: relationshipParameter || isQuickSearchParam,
      sortOptions: relationshipSortOrder || selectedSortOptions,
      columnOrder: selectedListColumnOrder,
      groupByParameters: groupByParams,
      groupByActive: isGroupActive,
      isCurrentRate: formRequest?.isCurrentRate?.toString(),
      isNonBandRate: formRequest?.isNonBandRate?.toString(),
      isDistinct,
    };
  }
  return fltDetls;
};

export const onClickExport = ({ entityFilterConfig, listSortOptions, selectedFilter, listActive, emitExport, externalExport,
  listColumnOrder, relationshipParameter, listParamOptions, listGroupBy, relationshipSortOrder, locale, pageSize,
  exportFileName, dispatch, processingInProgress, isDistinctActive, quickSearchParam, isQuickSearchFilter,
  formRequest, currentPage }) => {
  if (entityFilterConfig.EXPORT_ACTION && !processingInProgress) {
    const selectedSortOptions = listSortOptions || selectedFilter.detail.sortOptions;
    const groupByParams = listGroupBy || selectedFilter.detail.groupByParameters;
    const isGroupActive = !!(groupByParams && groupByParams.length > 0);
    const selectedListColumnOrder = formatListCol({ listColumnOrder, selectedFilter, isGroupActive });
    const isDistinct = isDistinctActive || selectedFilter.detail?.isDistinct;
    const isQuickSearchParam = (isQuickSearchFilter === '1' ? quickSearchParam : listParamOptions);
    const reqParam = setParam(relationshipParameter || listParamOptions);
    const filterDetails = setFltDetails({ formRequest,
      relationshipSortOrder,
      selectedSortOptions,
      selectedListColumnOrder,
      groupByParams,
      isGroupActive,
      isDistinct,
      isQuickSearchParam,
      relationshipParameter: reqParam,
    });
    const exportType = 'ALL';
    const reqListObject = {
      data: { ...getListRequest({ filterDetails, locale, exportType, listActive, customPageSize: pageSize, customCurrentPage: currentPage }) },
      columnOrders: formatOrder(selectedListColumnOrder),
      fileName: exportFileName,
      sheetName: '',
    };
    if (externalExport) {
      emitExport(reqListObject);
    } else {
      dispatch(getAction(entityFilterConfig.EXPORT_ACTION, reqListObject));
    }
  }
};

export const privateClickHandler = (val, setIsPrivate, privateHandler) => {
  setIsPrivate(val);
  privateHandler(val);
};

export const configPrivate = (sharedFilter, isPrivate, setIsPrivate) => {
  if (sharedFilter && isPrivate) {
    document.getElementById('isPrivate').click();
    setIsPrivate(false);
  }
};

export const SetDeleteEnabled = (cpStore, enableDeleteState) => (cpStore ? enableDeleteState : false);

export const setDelRefEnbl = (deleteEnabled, cpStore, enableDeleteState) => ((deleteEnabled) || SetDeleteEnabled(cpStore, enableDeleteState));

export const setDelRefLbl = (deleteLabel, t) => ((deleteLabel !== '')
  ? deleteLabel : t(staticCommonLabelKeys.COMMON_BUTTON_DELETE));

/**
 * Button render functions
 */
export const showFasatChip = (defaultChipText) => (
  <FasatChip
    data={defaultChipText}
    hasCloseBtn={false}
    isDefaultChip
  />
);

export const showResetActionBtn = (resetEnabled, onClickReset, resetLabel, resetAriaLabel) => (
  <ActionButton
    id="resetActionButton"
    disabled={!resetEnabled}
    aria-label={resetAriaLabel}
    icon={(
      <Reload
        color={reLdExtCls(resetEnabled, colors)}
        style={{ width: '20px', height: '20px', marginTop: '5px' }}
        aria-hidden="true"
      />
          )}
    hoverIcon={(
      <Reload
        color={reLdHvExtCls(resetEnabled, colors)}
        style={{ width: '20px', height: '20px', marginTop: '5px' }}
        aria-hidden="true"
      />
          )}
    onClick={onClickReset}
  >
    {resetLabel}
  </ActionButton>
);

export const showApproveActionBtn = (approveAclKey, approveEnabled, approveLabel, onClickApprove, rbacData) => (
  <FasatRBAC aclKey={approveAclKey} userClaim={rbacData}>
    <ActionButton
      id="approveActionButton"
      disabled={!approveEnabled}
      aria-label={approveLabel}
      icon={(
        <ButtonCheckmarkOutlined
          color={reLdExtCls(approveEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      hoverIcon={(
        <ButtonCheckmarkOutlined
          color={reLdHvExtCls(approveEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickApprove}
    >
      {approveLabel}
    </ActionButton>
  </FasatRBAC>
);

export const showEditActionBtn = (editAclKey, rbacData, onClickEdit, t) => (
  <FasatRBAC aclKey={editAclKey} userClaim={rbacData}>
    <ActionButton
      id="ptEditBtn"
      aria-label={t(staticCommonLabelKeys.COMMON_BUTTON_EDIT)}
      hoverIcon={<Edit color={colors.dark_2_green} aria-hidden="true" />}
      icon={<Edit color={colors.m_green} aria-hidden="true" />}
      onClick={onClickEdit}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_EDIT)}
    </ActionButton>
  </FasatRBAC>
);

export const showReverseActionBtn = (reverseAclKey, activateReprocessButton, rbacData, onClickReverse, t) => (
  <FasatRBAC aclKey={reverseAclKey} userClaim={rbacData}>
    <ActionButton
      id="ptReverseBtn"
      disabled={!activateReprocessButton}
      aria-label={t(staticCommonLabelKeys.COMMON_BUTTON_REVERSE)}
      icon={(
        <RollOverFunds
          color={icnExtCls(activateReprocessButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      hoverIcon={(
        <RollOverFunds
          color={icnHvExtCls(activateReprocessButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickReverse}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_REVERSE)}
    </ActionButton>
  </FasatRBAC>
);

export const showReprocessActionBtn = (reprocessAclKey, activateReprocessButton, rbacData, onClickReprocess, t) => (
  <FasatRBAC aclKey={reprocessAclKey} userClaim={rbacData}>
    <ActionButton
      id="ptReprocessBtn"
      disabled={!activateReprocessButton}
      aria-label={t(staticCommonLabelKeys.COMMON_BUTTON_REPROCESS)}
      icon={(
        <FundSwitches
          color={icnExtCls(activateReprocessButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      hoverIcon={(
        <FundSwitches
          color={icnHvExtCls(activateReprocessButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickReprocess}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_REPROCESS)}
    </ActionButton>
  </FasatRBAC>
);

export const showBulkActionBtn = (reprocessAclKey, activateBulkButton, rbacData, onClickBulk, t) => (
  <FasatRBAC aclKey={reprocessAclKey} userClaim={rbacData}>
    <ActionButton
      id="ptBulkBtn"
      disabled={!activateBulkButton}
      aria-label={t(staticCommonLabelKeys.COMMON_BUTTON_BULK)}
      icon={(
        <FundSwitches
          color={icnExtCls(activateBulkButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      hoverIcon={(
        <FundSwitches
          color={icnHvExtCls(activateBulkButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickBulk}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_BULK_REPROCESS)}
    </ActionButton>
  </FasatRBAC>
);

export const showProcessNowActionBtn = (processNowAclKey, activateAUPButton, rbacData, onClickProcessNow, t) => (
  <FasatRBAC aclKey={processNowAclKey} userClaim={rbacData}>
    <ActionButton
      id="ptReprocessBtn"
      disabled={!activateAUPButton}
      aria-label={t(staticCommonLabelKeys.COMMON_BUTTON_PROCESS_NOW)}
      icon={(
        <Coin
          color={icnExtCls(activateAUPButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      hoverIcon={(
        <Coin
          color={icnHvExtCls(activateAUPButton, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickProcessNow}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_PROCESS_NOW)}
    </ActionButton>
  </FasatRBAC>
);

export const showChangeStatusActionBtn = (
  t,
  changeStatusAclKey,
  onClickChangeStatus,
  changeStatusLabel,
  changeStatusEnabled,
  rbacData,
) => (
  <FasatRBAC aclKey={changeStatusAclKey} userClaim={rbacData}>
    <ActionButton
      id="showChangeStatusActionBtn"
      disabled={!changeStatusEnabled}
      aria-label={t(changeStatusLabel)}
      icon={(<Write color={reLdExtCls(changeStatusEnabled, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      hoverIcon={(
        <Write
          color={reLdHvExtCls(changeStatusEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      onClick={onClickChangeStatus}
    >
      {t(changeStatusLabel)}
    </ActionButton>
  </FasatRBAC>
);

export const showSelectLetterActionBtn = (
  t,
  selectLetterAclKey,
  onClickSelectLetter,
  selectLetterLabel,
  selectLetterEnabled,
  rbacData,
) => (
  <FasatRBAC aclKey={selectLetterAclKey} userClaim={rbacData}>
    <ActionButton
      id="showselectLetterActionBtn"
      disabled={!selectLetterEnabled}
      aria-label={t(selectLetterLabel)}
      icon={(<Email color={reLdExtCls(selectLetterEnabled, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      hoverIcon={(
        <Email
          color={reLdHvExtCls(selectLetterEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      onClick={onClickSelectLetter}
    >
      {t(selectLetterLabel)}
    </ActionButton>
  </FasatRBAC>
);

export const showAddActionBtn = (addAclKey, rbacData, onClickAdd, t, addBtnAriaLabel, pdDisableAdd) => (
  <FasatRBAC aclKey={addAclKey} userClaim={rbacData}>
    <ActionButton
      aria-label={addBtnAriaLabel || t(staticCommonLabelKeys.COMMON_BUTTON_ADD)}
      disabled={pdDisableAdd}
      icon={<FasatIcon manulifeName="plus1" fill={icnAddCls(pdDisableAdd, colors)} aria-hidden="true" />}
      hoverIcon={<FasatIcon manulifeName="plus1" fill={icnHvAddCls(pdDisableAdd, colors)} aria-hidden="true" />}
      onClick={onClickAdd}
      id="addNewButton"
      data-testid="addNewButton-label"
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_ADD)}
    </ActionButton>
  </FasatRBAC>
);

export const showCloneActionBtn = (addAclKey, rbacData, onClickAdd, t, addBtnAriaLabel, pdDisableAdd) => (
  <FasatRBAC aclKey={addAclKey} userClaim={rbacData}>
    <ActionButton
      aria-label={addBtnAriaLabel || t(staticLabelKeys.SWB_HEADER)}
      disabled={pdDisableAdd}
      icon={(
        <Duplicate
          color={icnAddCls(pdDisableAdd, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
)}
      hoverIcon={(
        <Duplicate
          color={icnHvAddCls(pdDisableAdd, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickAdd}
      id="addNewButton"
      data-testid="addNewButton-label"
    >
      {t(staticLabelKeys.SWB_HEADER)}
    </ActionButton>
  </FasatRBAC>
);

export const showDeleteActionBtn = (deleteAclKey, deleteEnabled, deleteLabel, onClickDelete, rbacData) => (
  <FasatRBAC aclKey={deleteAclKey} userClaim={rbacData}>
    <ActionButton
      id="deleteActionButton"
      disabled={!deleteEnabled}
      aria-label={deleteLabel}
      icon={(<Delete color={reLdExtCls(deleteEnabled, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      hoverIcon={(<Delete color={reLdHvExtCls(deleteEnabled, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      onClick={onClickDelete}
    >
      {deleteLabel}
    </ActionButton>
  </FasatRBAC>
);

export const showImportActionBtn = (importAclKey, importEnabled, importLabel, onClickImport, rbacData) => (
  <FasatRBAC aclKey={importAclKey} userClaim={rbacData}>
    <ActionButton
      id="importActionButton"
      disabled={!importEnabled}
      aria-label={importLabel}
      icon={(<Import color={reLdExtCls(importEnabled, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      hoverIcon={(<Import color={reLdHvExtCls(importEnabled, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      onClick={onClickImport}
    >
      {importLabel}
    </ActionButton>
  </FasatRBAC>
);

export const showExportActionBtn = ({ exportAclKey, rbacData, activateExportButton, t, onClickExportAction, exportBtnID }) => (
  <FasatRBAC aclKey={exportAclKey} userClaim={rbacData}>
    <ActionButton
      id={exportBtnID}
      disabled={!activateExportButton}
      aria-label={t(staticCommonLabelKeys.ARIA_COMMON_BUTTON_EXPORT_LIST)}
      icon={<Export color={icnExtCls(activateExportButton, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />}
      hoverIcon={(<Export color={icnHvExtCls(activateExportButton, colors)} style={{ width: '20px', height: '20px' }} aria-hidden="true" />)}
      onClick={onClickExportAction}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_EXPORT)}
    </ActionButton>
  </FasatRBAC>
);

export const displayPageSizePart1 = (t) => (
  <span data-testid="pageShow-label">
    {t(staticCommonLabelKeys.COMMON_LABEL_PAGESIZE_PART1)}
    &nbsp;
  </span>
);

export const displayPageSizePart2 = (t) => (
  <span data-testid="page-records-label">
    &nbsp;
    {t(staticCommonLabelKeys.COMMON_LABEL_PAGESIZE_PART2)}
  </span>
);

export const itemPerPage = ({ pageSizeOptions, onChangePageSize, pageSize, t, pageSizeID }) => (
  <span className="pageSizeDropdown">
    <FasatDropdown
      ariaLabel={t(staticCommonLabelKeys.COMMON_LABEL_PAGESIZE_PART1)}
      dropdownItems={pageSizeOptions}
      id={pageSizeID}
      onHandleChange={onChangePageSize}
      placeholder={t(staticCommonLabelKeys.COMMON_PLACEHOLDER_SELECT)}
      value={`${pageSize}`}
      variant="basic"
      customStyle={{
        rootStyle: {
          width: 'auto',
        },
      }}
    />
  </span>
);

export const showRemitPayment = (remitAclKey, approveEnabled, remitLabel, onClickRemit, rbacData) => (
  <FasatRBAC aclKey={remitAclKey} userClaim={rbacData}>
    <ActionButton
      id="approveRemitButton"
      disabled={!approveEnabled}
      aria-label={remitLabel}
      icon={(
        <Coin
          color={reLdExtCls(approveEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      hoverIcon={(
        <Coin
          color={reLdHvExtCls(approveEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={onClickRemit}
    >
      {remitLabel}
    </ActionButton>
  </FasatRBAC>
);

export const showDownloadButton = (downloadBtn, rbacData) => (
  <FasatRBAC aclKey={downloadBtn.downloadAclKey} userClaim={rbacData}>
    <ActionButton
      id="downloadActionButton"
      disabled={!downloadBtn.enableDownload}
      aria-label={downloadBtn.downloadLabel}
      icon={(
        <Download
          color={reLdExtCls(downloadBtn.enableDownload, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      hoverIcon={(
        <Download
          color={reLdHvExtCls(downloadBtn.enableDownload, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={downloadBtn.onClickDownload}
    >
      {downloadBtn.downloadLabel}
    </ActionButton>
  </FasatRBAC>
);

export const showPrintButton = (printBtn, rbacData) => (
  <FasatRBAC aclKey={printBtn.printAclKey} userClaim={rbacData}>
    <ActionButton
      id="printActionButton"
      disabled={!printBtn.enablePrint}
      aria-label={printBtn.printLabel}
      icon={(
        <Print
          color={reLdExtCls(printBtn.enablePrint, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      hoverIcon={(
        <Print
          color={reLdHvExtCls(printBtn.enablePrint, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={printBtn.onClickPrint}
    >
      {printBtn.printLabel}
    </ActionButton>
  </FasatRBAC>
);

export const showViewButton = (viewBtn, rbacData) => (
  <FasatRBAC aclKey={viewBtn.viewAclKey} userClaim={rbacData}>
    <ActionButton
      id="viewActionButton"
      disabled={!viewBtn.enableBtn}
      aria-label={viewBtn.label}
      icon={(
        <Eye1
          color={reLdExtCls(viewBtn.enableBtn, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      hoverIcon={(
        <Eye1
          color={reLdHvExtCls(viewBtn.enableBtn, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
            )}
      onClick={viewBtn.onClick}
    >
      {viewBtn.label}
    </ActionButton>
  </FasatRBAC>
);

export const checkOperation = ({ approveAclKey, approveAction, approveEnabled, approveLabel, onClickApprove, defaultChipText,
  resetAction, showDefaultChip, resetEnabled, onClickReset, resetLabel, rbacData, resetAriaLabel }) => (
    <>
      {approveAction && (showApproveActionBtn(approveAclKey, approveEnabled, approveLabel, onClickApprove, rbacData))}
      {showDefaultChip && (showFasatChip(defaultChipText))}
      {resetAction && (showResetActionBtn(resetEnabled, onClickReset, resetLabel, resetAriaLabel))}
    </>
);

export const showCancelActionBtn = (cancelEnabled, onClickCancel, t, cancelAclKey, rbacData) => (
  <FasatRBAC aclKey={cancelAclKey} userClaim={rbacData}>
    <ActionButton
      id="cancelActionButton"
      disabled={!cancelEnabled}
      aria-label={t(staticCommonLabelKeys.COMMON_BUTTON_CANCEL)}
      icon={(
        <ButtonXOutlined
          color={reLdExtCls(cancelEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
              )}
      hoverIcon={(
        <ButtonXOutlined
          color={reLdHvExtCls(cancelEnabled, colors)}
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
              )}
      onClick={onClickCancel}
    >
      {t(staticCommonLabelKeys.COMMON_BUTTON_CANCEL)}
    </ActionButton>
  </FasatRBAC>
);

export function BackButton(backBtnParams, t) {
  const { showBtn, onClick, label } = backBtnParams;
  return (
    <>
      {!!showBtn
      && (
      <ActionButton
        id="backBttn"
        aria-label={t(label)}
        icon={(
          <ArrowLeft
            color={colors.red_error}
            style={{ width: '20px', height: '20px' }}
            aria-hidden="true"
          />
        )}
        hoverIcon={(
          <ArrowLeft
            color={reLdHvExtCls(true, colors)}
            style={{ width: '20px', height: '20px' }}
            aria-hidden="true"
          />
        )}
        onClick={onClick}
      >
        {t(label)}
      </ActionButton>
      )}
    </>
  );
}

export const showPageSection = ({ showPageSize, t, pageSizeOptions, onChangePageSize, pageSize, pageSizeID, backBtnParams }) => (
  <div className="pageSizeSection">
    {backBtnParams && BackButton(backBtnParams, t)}
    {showPageSize && (
    <>
      {displayPageSizePart1(t)}
      {itemPerPage({ pageSizeOptions, onChangePageSize, pageSize, t, pageSizeID })}
      {displayPageSizePart2(t)}
    </>
    )}
  </div>
);

export function ButtonOperation({ downloadBtn, printBtn, rbacData, viewBtn }) {
  return (
    <>
      {viewBtn?.action && (showViewButton(viewBtn, rbacData))}
      {downloadBtn?.downloadAction && (showDownloadButton(downloadBtn, rbacData))}
      {printBtn?.printAction && (showPrintButton(printBtn, rbacData))}
    </>
  );
}

export function ApprovePaymentBtn({ approvePayment, approveAclKey, approveEnabled, approveLabel, onClickApprove, rbacData, remitAclKey, remitLabel, onClickRemit, onClickCancel, t, cancelAclKey }) {
  return (
    <>
      {approvePayment && (showApproveActionBtn(approveAclKey, approveEnabled, approveLabel, onClickApprove, rbacData))}
      {approvePayment && (showRemitPayment(remitAclKey, approveEnabled, remitLabel, onClickRemit, rbacData))}
      {approvePayment && (showCancelActionBtn(approveEnabled, onClickCancel, t, cancelAclKey, rbacData))}
    </>
  );
}

export function ActivityProcessBtn({ reverseAction, reprocessAction, processNowAction, reverseAclKey, activateReprocessButton, rbacData, onClickReverse, t, reprocessAclKey, onClickReprocess, processNowAclKey, activateAUPButton,
  onClickProcessNow,
  onClickBulk,
  bulkAction,
  activateBulkButton }) {
  return (
    <>
      {reverseAction && (showReverseActionBtn(reverseAclKey, activateReprocessButton, rbacData, onClickReverse, t))}
      {reprocessAction && (showReprocessActionBtn(
        reprocessAclKey,
        activateReprocessButton,
        rbacData,
        onClickReprocess,
        t,
      ))}
      {processNowAction && (showProcessNowActionBtn(
        processNowAclKey,
        activateAUPButton,
        rbacData,
        onClickProcessNow,
        t,
      ))}
      {bulkAction && (showBulkActionBtn(reprocessAclKey, activateBulkButton, rbacData, onClickBulk, t))}
    </>
  );
}

export function TerminatedRecordButton(terminatedRecordBtnParams, t) {
  const { showBtn, onClick, showText, hideText, showTerminatedRecords } = terminatedRecordBtnParams;
  return (
    <>
      {showBtn && (
      <>
        {showTerminatedRecords && (
        <ActionButton
          id="showTerminatedRecordsActionButton"
          aria-label={t(hideText)}
          icon={(
            <Eye2
              color={reLdExtCls(true, colors)}
              style={{ width: '20px', height: '20px' }}
              aria-hidden="true"
            />
              )}
          hoverIcon={(
            <Eye2
              color={reLdHvExtCls(true, colors)}
              style={{ width: '20px', height: '20px' }}
              aria-hidden="true"
            />
              )}
          onClick={onClick}
        >
          {t(hideText)}
        </ActionButton>
        )}
        {!showTerminatedRecords && (
        <ActionButton
          id="hideTerminatedRecordsActionButton"
          aria-label={t(showText)}
          icon={(
            <Eye1
              color={reLdExtCls(true, colors)}
              style={{ width: '20px', height: '20px' }}
              aria-hidden="true"
            />
              )}
          hoverIcon={(
            <Eye1
              color={reLdHvExtCls(true, colors)}
              style={{ width: '20px', height: '20px' }}
              aria-hidden="true"
            />
              )}
          onClick={onClick}
        >
          {t(showText)}
        </ActionButton>
        )}
      </>
      )}
    </>
  );
}
